import React from 'react';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiShield,  FiMail,  FiGlobe } from 'react-icons/fi';

const CollegeAdmissions = () => {
  const trends = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Online Applications Are Now Standard',
      description: 'In 2026, online applications are no longer optional. They are expected. Students and parents demand the convenience of applying from home, at any time. Institutions without online applications are losing applicants to competitors.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'AI-Powered Application Screening',
      description: 'Artificial intelligence is transforming admission screening. AI tools can analyze applications, assess student profiles, and identify the best candidates. This speeds up the process and reduces human bias.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Automated Communication Throughout',
      description: 'Students expect regular updates during the admission process. Automated emails and SMS keep them informed at every stage—application received, documents verified, interview scheduled, offer made.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Personalized Application Experiences',
      description: 'One-size-fits-all applications are outdated. Students expect personalized experiences based on their profile, program choice, and preferences. Smart forms adapt to each applicant.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Data-Driven Admission Decisions',
      description: 'Colleges are using data analytics to make better admission decisions. They analyze application trends, conversion rates, and student success patterns to optimize their admission strategies.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Faster Decision-Making',
      description: 'Students expect decisions within days, not weeks. Institutions that make faster decisions have a competitive advantage. Automated workflows reduce the time from application to offer.',
      color: 'bg-blue-50'
    }
  ];

  const steps = [
    {
      title: 'Step 1: Pre-Application Phase',
      description: 'Students research colleges, compare programs, and check eligibility. Your website and admission portal should provide clear information about programs, fees, eligibility, and deadlines.'
    },
    {
      title: 'Step 2: Application Submission',
      description: 'The application form should be simple, intuitive, and mobile-friendly. Ask only for essential information. Use smart forms that adapt based on student responses.'
    },
    {
      title: 'Step 3: Document Upload and Verification',
      description: 'Provide clear guidance on required documents, formats, and sizes. Enable auto-verification where possible. Allow document resubmission if needed.'
    },
    {
      title: 'Step 4: Payment and Fee Processing',
      description: 'Integrate multiple payment options—credit/debit cards, net banking, UPI, and digital wallets. Ensure secure payment processing with instant confirmation.'
    },
    {
      title: 'Step 5: Evaluation and Decision',
      description: 'Automate the evaluation process where possible. Use AI screening tools to shortlist candidates. Involve faculty in final decisions through digital workflows.'
    },
    {
      title: 'Step 6: Offer and Enrollment',
      description: 'Send automated admission offers. Provide clear next steps for enrollment. Track acceptance rates and enrollment conversion. Onboard students seamlessly.'
    }
  ];

  const benefits = [
    {
      title: 'Faster Admission Processing',
      description: 'Digital processes reduce admission timelines by up to 70%. Decisions in days, not weeks.'
    },
    {
      title: 'Better Student Experience',
      description: 'Students appreciate the convenience and transparency of digital admissions. It creates a positive impression.'
    },
    {
      title: 'Higher Conversion Rates',
      description: 'Reduce drop-offs with simpler processes, better communication, and faster decisions.'
    },
    {
      title: 'Reach More Students',
      description: 'Digital admissions enable you to reach students across India and internationally.'
    },
    {
      title: 'Improved Brand Reputation',
      description: 'A modern, efficient admission process builds your institution\'s reputation and attracts more applicants.'
    },
    {
      title: 'Data-Driven Insights',
      description: 'Analyze admission trends, identify bottlenecks, and make informed decisions about your admission strategy.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        College admissions in India are changing faster than ever before. In 2026, the days of paper forms, long queues, and anxious waiting are ending. Students and parents expect a digital experience—smooth, transparent, and fast.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">The institutions that are winning the admissions race are those that have embraced digital transformation.</strong> They've automated processes, simplified forms, improved communication, and made it easy for students to apply. The result? More applications, better conversion rates, and stronger brands.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Whether you're a PU college, a degree college, or an autonomous institution, this guide will help you navigate the digital admission landscape in 2026.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Faster admissions with digital processes</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">60%</div>
          <div className="text-sm text-slate-500">Higher application completion rates</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%</div>
          <div className="text-sm text-slate-500">Students prefer digital admissions</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Trends Shaping College Admissions in 2026</h2>

      {/* Trends List */}
      {trends.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* The Digital Admission Process */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Digital Admission Process: Step by Step</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {steps.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold text-sm">{i + 1}.</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Digital Admissions Are the Future</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Digital Admissions in 2026</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Admissions</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Paper forms and physical queues</span></li>
              <li className="flex items-start gap-2">• <span>Manual data entry</span></li>
              <li className="flex items-start gap-2">• <span>No application status visibility</span></li>
              <li className="flex items-start gap-2">• <span>Slow decision-making (weeks to months)</span></li>
              <li className="flex items-start gap-2">• <span>Limited applicant reach</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Digital Admissions with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Online, mobile-friendly applications</span></li>
              <li className="flex items-start gap-2">• <span>Automated data entry and verification</span></li>
              <li className="flex items-start gap-2">• <span>Real-time application tracking</span></li>
              <li className="flex items-start gap-2">• <span>Fast decisions (days, not weeks)</span></li>
              <li className="flex items-start gap-2">• <span>National and international reach</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is Your Partner for Digital Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Smart Application Forms</h4>
            <p className="text-xs text-slate-600">5-minute applications with auto-fill and smart logic</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Document Management</h4>
            <p className="text-xs text-slate-600">Clear guidance and automated verification</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly</h4>
            <p className="text-xs text-slate-600">Apply from any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Automated Communication</h4>
            <p className="text-xs text-slate-600">Real-time status updates and notifications</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Decisions</h4>
            <p className="text-xs text-slate-600">Automated workflows reduce decision time by 70%</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics Dashboard</h4>
            <p className="text-xs text-slate-600">Track applications, conversion, and trends</p>
          </div>
        </div>
      </div>

      {/* The Student Journey Map */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Modern Student Admission Journey</h3>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-200">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-xs">Research</h4>
            <p className="text-xs text-slate-600">Explore programs</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-xs">Apply</h4>
            <p className="text-xs text-slate-600">Submit application</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-xs">Verify</h4>
            <p className="text-xs text-slate-600">Upload documents</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-200">
            <div className="text-2xl mb-1">💳</div>
            <h4 className="font-bold text-slate-900 text-xs">Pay</h4>
            <p className="text-xs text-slate-600">Complete payment</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-200">
            <div className="text-2xl mb-1">🎉</div>
            <h4 className="font-bold text-slate-900 text-xs">Accept</h4>
            <p className="text-xs text-slate-600">Receive offer</p>
          </div>
        </div>
      </div>

      {/* The Institutional Perspective */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">What Institutions Gain from Digital Admissions</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Digital admissions aren't just about keeping up with trends. They deliver real, measurable benefits to institutions.
        </p>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Fill seats faster with automated processes</span></li>
          <li className="flex items-start gap-2">• <span>Reduce administrative workload and costs</span></li>
          <li className="flex items-start gap-2">• <span>Improve applicant experience and brand reputation</span></li>
          <li className="flex items-start gap-2">• <span>Make better decisions with real-time data and analytics</span></li>
          <li className="flex items-start gap-2">• <span>Reach more students across India and internationally</span></li>
          <li className="flex items-start gap-2">• <span>Stay competitive in a rapidly evolving education market</span></li>
        </ul>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Go Digital in 2026 with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          College admissions in India are changing. Students expect digital experiences. Institutions that embrace digital transformation are winning the race—filling seats faster, building stronger brands, and attracting the best students.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon makes the transition to digital admissions simple. Smart forms, automated verification, real-time tracking, integrated payments, and powerful analytics. Everything you need to modernize your admission process.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for digital admissions.</strong> Ready for 2026. Ready for the future.
        </p>
      </div>
    </>
  );
};

export default CollegeAdmissions;